// ════════════════════════════════════════════════════════════════════════
//  Welche Schalter eine Klasse an einem Lehrgang überhaupt hat
//
//  Reine Rechnung, ohne React und ohne Datenbank – dieselbe Bauart wie
//  `assign.ts` nebenan. Der Punkt ist die Sichtbarkeit: ein Schalter ohne
//  Wirkung ist eine Frage ohne Folgen. Wer den Test einer Fassung ausschaltet,
//  die gar keinen hat, hat nichts getan und weiß es nicht.
//
//  Der Inhalt ist die Möglichkeit, die Klasseneinstellung der Gebrauch. Diese
//  Datei beantwortet den ersten Teil: gibt es die Möglichkeit?
// ════════════════════════════════════════════════════════════════════════

import type { Bundle } from "@otto/lehrgang";
import type { CourseOption } from "./assign.js";

/**
 * Hat die Fassung, die diese Klasse übt, einen Test?
 *
 * Gefragt wird die **zugeordnete** Fassung, nicht die neueste. Eine Klasse auf
 * Fassung 3 hat den Test, den Fassung 3 hatte – auch wenn Fassung 4 ihn längst
 * wieder losgeworden ist.
 *
 * Ein nicht lesbares Bundle (`null`) zählt als „kein Test“. Das versteckt den
 * Schalter, sagt aber die Wahrheit über das, was diese App weiß; die kaputte
 * Fassung selbst ist ein eigenes Problem und wird eigens angezeigt.
 */
export function hasAssessment(course: { readonly bundle: Bundle | null }): boolean {
  return course.bundle?.assessment !== undefined;
}

/**
 * Der Vorgänger dieses Lehrgangs, wie er im Katalog heißt – oder null.
 *
 * Null auch dann, wenn es einen `requiresId` gibt, der Katalog ihn aber nicht
 * kennt (stillgelegt, nie veröffentlicht). Dann bleibt der Schalter weg: ein
 * Haken, dessen Text auf einen Lehrgang zeigt, den die Lehrkraft nirgends
 * findet, ist keine Einstellung, sondern ein Rätsel. Dieselbe Regel wie in
 * `courseOptionLabel`.
 */
export function predecessorOf(
  course: { readonly requiresId: string | null },
  catalog: readonly CourseOption[],
): CourseOption | null {
  if (course.requiresId === null) return null;
  return catalog.find((o) => o.curriculumId === course.requiresId) ?? null;
}

/**
 * Gibt es eine neuere veröffentlichte Fassung als die, die diese Klasse übt?
 *
 * Liefert deren Nummer, sonst null. Sie ist eine Mitteilung, keine Aufgabe: die
 * Klasse wechselt nicht mehr (die Fassung steht fest, sobald das erste Kind
 * geübt hat), und neue Klassen bekommen die neuere von selbst.
 *
 * Verglichen wird die NUMMER, nicht die Kennung. Zwei Zeilen mit derselben
 * Nummer gibt es je Lehrgang nicht, und die Nummer ist das, was auf dem Schild
 * steht.
 */
export function newerVersionOf(
  course: { readonly curriculumId: string; readonly version: number },
  catalog: readonly CourseOption[],
): number | null {
  const entry = catalog.find((o) => o.curriculumId === course.curriculumId);
  const latest = entry?.latestVersion ?? null;
  return latest !== null && latest > course.version ? latest : null;
}
